<script setup>
import { ref, computed, watch, onBeforeMount } from 'vue'
import { useProfileStore } from '@/stores/profile'
import { useTranslaterStore } from '@/stores/translater'
import { useRequestsStore } from '@/stores/requests'
import checkArrayIncludes from '@/helpers/checkArrayIncludes'
import UTALang from '@/components/UTALang.vue'
import UTAHeaderProfile from '@/components/UTAHeaderProfile.vue'
import { useRoute } from 'vue-router'

const route = useRoute()

const translaterStore = useTranslaterStore()
const profileStore = useProfileStore()
const requestsStore = useRequestsStore()

const profile = computed(() => profileStore.profile)
const getTranslate = computed(() => translaterStore.getTranslate)

const isOpen = ref(false)

onBeforeMount(() => {
    window.addEventListener('resize', function (event) {
        if(event.target.innerWidth > 1199 && isOpen.value == true) {
            isOpen.value = false
        }
    })
})

watch(() => route.path,
    () => {
        if (isOpen.value) {
            isOpen.value = false
            document.body.style = ''
        }
    }
)

watch(isOpen, (newVal) => {
    const scrollWidth = document.body.offsetWidth - document.body.clientWidth
    
    const page = document.querySelector('.page')
    const top = document.querySelector('.uta-top')
    const isPageNavOpen = page?.classList.contains('page--translatex')

    if(newVal == true) {
        if(document.body.scrollTop > 0)
            top.scrollIntoView({ block: "start", behavior: "smooth" })

        document.body.style.overflow = 'hidden'

        if(scrollWidth > 0) document.body.style.paddingRight = scrollWidth + 'px'
    } else {
        if(!isPageNavOpen) document.body.style = ''
    }
})
</script>

<template>
    <header class="uta-top">
        <nav class="navbar navbar-expand-xl uta-top__navbar">
            <RouterLink to="/" class="navbar-brand uta-top__navbar-brand">
                <img class="uta-top__navbar-logo" src="@/assets/images/logo.png" alt="">
            </RouterLink>
            <button 
                type="button" 
                class="uta-top__navbar-btn d-block d-xl-none" 
                :class="{ 'uta-top__navbar-btn--opened': isOpen }"
                @click="isOpen = !isOpen"
            >
            <svg width="60" height="60" viewBox="0 0 100 100">
                <path class="line line1" d="M 20,29.000046 H 80.000231 C 80.000231,29.000046 94.498839,28.817352 94.532987,66.711331 94.543142,77.980673 90.966081,81.670246 85.259173,81.668997 79.552261,81.667751 75.000211,74.999942 75.000211,74.999942 L 25.000021,25.000058" />
                <path class="line line2" d="M 20,50 H 80" />
                <path class="line line3" d="M 20,70.999954 H 80.000231 C 80.000231,70.999954 94.498839,71.182648 94.532987,33.288669 94.543142,22.019327 90.966081,18.329754 85.259173,18.331003 79.552261,18.332249 75.000211,25.000058 75.000211,25.000058 L 25.000021,74.999942" />
                </svg>
            </button>
            <transition name="slidex">
            <div v-show="isOpen" class="uta-top__navbar-offcanvas" :class="{ 'uta-top__navbar-offcanvas--opened': isOpen }">
                <div class="d-flex flex-column flex-xl-row w-100 h-100 position-relative">
                    <ul class="navbar-nav uta-navi order-1 order-xl-0">
                        <li class="nav-item">
                            <RouterLink to="/cars" class="nav-link uta-navi__link">
                                <span class="uta-navi__link-icon">
                                    <span class="material-icons">
                                        directions_car
                                    </span>
                                </span>
                                <span class="uta-navi__link-text">{{ getTranslate.MENU_CARS }}</span>
                            </RouterLink>
                        </li>
                        <li class="nav-item" v-if="checkArrayIncludes(profile.attr?.array, ['SUPER_ADMIN'])">
                            <RouterLink to="/firmwares" class="nav-link uta-navi__link">
                                <span class="uta-navi__link-icon">
                                    <span class="material-icons">
                                        upload
                                    </span>
                                </span>
                                <span class="uta-navi__link-text">{{ getTranslate.MENU_FIRMWARES }}</span>
                            </RouterLink>
                        </li>
                        <li class="nav-item">
                            <RouterLink to="/areas" class="nav-link uta-navi__link">
                                <span class="uta-navi__link-icon">
                                    <span class="material-symbols-outlined">
                                        activity_zone
                                    </span>
                                </span>
                                <span class="uta-navi__link-text">{{ getTranslate.AREAS }}</span>
                            </RouterLink>
                        </li>
                        <li class="nav-item" v-if="checkArrayIncludes(profile.attr?.array, ['SUPER_ADMIN', 'GROUP_ADMIN'])">
                            <RouterLink to="/users" class="nav-link uta-navi__link">
                                <div class="uta-navi__link-icon">
                                    <span class="material-icons">
                                        group
                                    </span>
                                </div>
                                <span class="uta-navi__link-text">{{ getTranslate.MENU_DRIVERS }}</span>
                            </RouterLink>
                        </li>
                        <li class="nav-item d-xl-none">
                            <a href="/#" @click.prevent="requestsStore.logout()" class="nav-link uta-navi__link">
                                <span class="uta-navi__link-icon">
                                    <span class="material-icons">
                                        logout
                                    </span>
                                </span>
                                <span class="uta-navi__link-text">{{ getTranslate.PROFILE_LOGOUT }}</span>
                            </a>
                        </li>                  
                    </ul>
                    <div class="order-2 order-xl-1 d-flex align-items-center">
                        <UTALang />
                    </div>
                    <div class="order-0 order-xl-2 d-flex align-items-center justify-content-center">
                        <UTAHeaderProfile />
                    </div>
                </div>
            </div>
            </transition>
            <transition name="fade">
                <div v-if="isOpen" class="uta-top__navbar-offcanvas-backdrop" @click="isOpen = false"></div>
            </transition>
        </nav>
    </header>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/header';
@import '@/assets/styles/scss/components/navi';
</style>